/**
 * F09 v2 — small shared pieces for the admin verification module: status /
 * strength / age badges and the error banner (prerequisite + missing-document
 * lists).
 */
import { useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Badge, Banner } from '@bravemove/ui-web';
import type {
  VerifiableEntityType,
  VerificationRequestStatus,
  VerificationRiskFlag,
} from '@bravemove/types';
import {
  ageInDays,
  ageTone,
  entityLabel,
  prerequisiteLabel,
  requestStatusLabel,
  requestStatusTone,
  strengthFor,
  strengthLabel,
  strengthTone,
} from './labels';
import {
  missingGroupsOf,
  prerequisiteFailuresOf,
  verificationErrorCode,
  verificationErrorMessage,
} from './verificationErrors';

export function RequestStatusBadge({ status }: { readonly status: VerificationRequestStatus }) {
  const { t } = useI18n();
  return (
    <Badge tone={requestStatusTone(status)} size="sm" data-testid={`request-status-${status}`}>
      {requestStatusLabel(t, status)}
    </Badge>
  );
}

export function StrengthBadge({
  score,
  riskFlags,
}: {
  readonly score: number | null;
  readonly riskFlags: readonly VerificationRiskFlag[];
}) {
  const { t } = useI18n();
  const strength = strengthFor(score, riskFlags);
  return (
    <span className="inline-flex items-center gap-1.5" data-testid="strength-cell">
      <Badge tone={strengthTone(strength)} size="sm" data-strength={strength}>
        {strengthLabel(t, strength)}
      </Badge>
      <span className="font-mono text-[11px] text-ink-3 tabular-nums">
        {score === null
          ? t('admin:verification.common.unscored')
          : t('admin:verification.common.score', { score })}
      </span>
    </span>
  );
}

export function AgeBadge({ since }: { readonly since: string | null }) {
  const { t } = useI18n();
  const [now] = useState(() => Date.now());
  const days = ageInDays(since, now);
  if (days === null)
    return <span className="text-ink-3">{t('admin:verification.common.none')}</span>;
  return (
    <Badge tone={ageTone(days)} size="sm">
      {days === 0
        ? t('admin:verification.age.today')
        : t('admin:verification.age.days', { count: days })}
    </Badge>
  );
}

export function EntityTypeBadge({ type }: { readonly type: VerifiableEntityType }) {
  const { t } = useI18n();
  return (
    <Badge tone="neutral" size="sm">
      {entityLabel(t, type)}
    </Badge>
  );
}

/**
 * Danger banner for a verification error. `PREREQUISITE_NOT_MET` lists the
 * unmet requirements; `MISSING_REQUIRED_DOCUMENTS` lists the missing groups.
 */
export function VerificationErrorBanner({
  error,
  testId = 'verification-error',
}: {
  readonly error: unknown;
  readonly testId?: string;
}) {
  const { t } = useI18n();
  const code = verificationErrorCode(error);
  const failures = code === 'PREREQUISITE_NOT_MET' ? prerequisiteFailuresOf(error) : [];
  const missing = code === 'MISSING_REQUIRED_DOCUMENTS' ? missingGroupsOf(error) : [];
  return (
    <div data-testid={testId} data-code={code ?? undefined}>
      <Banner tone="danger">
        <p>{verificationErrorMessage(error, t)}</p>
        {failures.length > 0 ? (
          <ul className="mt-1 list-disc ps-5" data-testid={`${testId}-prerequisites`}>
            {failures.map((code) => (
              <li key={code}>{prerequisiteLabel(t, code)}</li>
            ))}
          </ul>
        ) : null}
        {missing.length > 0 ? (
          <ul className="mt-1 list-disc ps-5">
            {missing.map((g) => (
              <li key={g.labelKey}>{t(g.labelKey)}</li>
            ))}
          </ul>
        ) : null}
      </Banner>
    </div>
  );
}
